import ConditionalWrap from "conditional-wrap"
import { Fragment } from "react"

import { marketingClient } from "@trueskin-web/apis"
import { Spinner, Tappable } from "@trueskin-web/components"
import { Radio } from "@trueskin-web/theme"

import RepurchaseButton from "./components/repurchase/repurchase-button"
import { PaymentProvider, usePaymentContext } from "./payment.context"
import { usePaymentMethods } from "./use-payment-methods.hooks"

const Methods = ({
  primaryPaymentMethod,
  paymentMethods,
  order,
  JustPurchase,
}) => {
  const {
    activePaymentMethod,
    setAddNewPaymentMethod,
    selectPaymentMethod,
    isPaymentProcessing,
  } = usePaymentContext()

  return paymentMethods?.length === 0 ? (
    <div
      sx={{
        textAlign: "center",
      }}
    >
      <Spinner />
    </div>
  ) : (
    <Fragment>
      {paymentMethods?.map(
        (
          {
            type,
            title,
            description,
            showDescription,
            logo: Logo,
            paymentMethod: PaymentMethod,
          },
          index
        ) => (
          <ConditionalWrap
            key={index}
            condition={activePaymentMethod !== type && !isPaymentProcessing}
            wrap={(children) => (
              <Tappable
                data-testid={`web-portal-summary-payment-method-${type.toLowerCase()}-btn`}
                onClick={() => {
                  selectPaymentMethod(paymentMethods[index].type)
                  marketingClient.sendEvent({
                    name: marketingClient.EVENT_TYPES_ENUM
                      .NATIVE_PAYMENT_METHOD_CLICKED,
                    data: {
                      paymentMethod: paymentMethods[index].type,
                      value: order.amount,
                      userId: order.customerId,
                      voucherCode: order.voucherCode,
                      discountGroup: order.discountGroupId,
                      presetId: order.presetId,
                    },
                  })
                  setAddNewPaymentMethod(true)
                }}
                sx={{
                  width: "100%",
                  whiteSpace: "initial",
                  textAlign: "left",
                  lineHeight: "inherit",
                }}
              >
                {children}
              </Tappable>
            )}
          >
            <div
              sx={{
                width: "100%",
                border: "1px solid",
                borderColor: "grey",
                borderTop: index !== 0 ? "none" : null,
                p: 4,
              }}
              id={`payment-method-${type.toLowerCase()}`}
            >
              <div
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Radio
                  readOnly
                  checked={
                    (activePaymentMethod && activePaymentMethod === type) ||
                    (!activePaymentMethod && primaryPaymentMethod.type === type)
                  }
                />

                <div
                  sx={{
                    letterSpacing: "0.08em",
                  }}
                >
                  {title}
                </div>

                {Logo ? <Logo sx={{ ml: "auto" }} /> : null}
              </div>

              {description ? (
                <div
                  sx={{
                    display:
                      showDescription ||
                      (activePaymentMethod && activePaymentMethod === type) ||
                      (!activePaymentMethod &&
                        primaryPaymentMethod.type === type)
                        ? "block"
                        : "none",
                    fontSize: 1,
                    color: showDescription ? "textLight" : "inherit",
                    mr: 7,
                    ml: 6,
                  }}
                >
                  {description}
                </div>
              ) : null}

              {activePaymentMethod === type ||
              (!activePaymentMethod && type === "partner_card") ? (
                <div
                  sx={{
                    mt: 4,
                  }}
                >
                  <PaymentMethod JustPurchase={JustPurchase} order={order} />
                </div>
              ) : null}
            </div>
          </ConditionalWrap>
        )
      )}
    </Fragment>
  )
}

export const PaymentMethods = ({
  order,
  primaryPaymentMethod,
  hidePaymentMethods,
  purchase,
  repurchaseButton,
  onError,
}) => {
  hidePaymentMethods = hidePaymentMethods || []
  if (
    !primaryPaymentMethod ||
    primaryPaymentMethod.gateway !== "partner_card"
  ) {
    // don't show partner card to users that don't already have it
    hidePaymentMethods.push("partner_card")
  }
  const paymentMethods = usePaymentMethods({ hidePaymentMethods })
  return (
    <PaymentProvider order={order} purchase={purchase} onError={onError}>
      <PaymentMethodsWrapper
        order={order}
        paymentMethods={paymentMethods}
        primaryPaymentMethod={primaryPaymentMethod}
        repurchaseButton={repurchaseButton}
      />
    </PaymentProvider>
  )
}

const PaymentMethodsWrapper = ({
  primaryPaymentMethod,
  order,
  paymentMethods,
  repurchaseButton,
}) => {
  const { addNewPaymentMethod } = usePaymentContext()
  const RepurchaseBtn = repurchaseButton || RepurchaseButton

  const showRepurchaseButton =
    !addNewPaymentMethod &&
    primaryPaymentMethod?.gateway !== "partner_card" && // partner card always shows its own "repurchase" button
    primaryPaymentMethod?.status === "valid" &&
    order?.amount > 0

  const JustPurchase = () => (
    <RepurchaseBtn primaryPaymentMethod={primaryPaymentMethod} order={order} />
  )

  return (
    <>
      <Methods
        primaryPaymentMethod={primaryPaymentMethod}
        order={order}
        JustPurchase={JustPurchase}
        paymentMethods={paymentMethods}
      />
      {showRepurchaseButton && (
        <div>
          <JustPurchase />
        </div>
      )}
    </>
  )
}
